// StateViews — socle réutilisable pour les états d'écran (Design System GoWele).
// Skeleton (loading), EmptyState, ErrorState, OfflineState. i18n-friendly (textes via
// props, défauts via common.states.*), responsive, accessibles (role/aria).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Palette
const NAVY = "#1e3a6e";
const PRIMARY = "#1a4fc8";

// Injecte le keyframe de shimmer une seule fois.
const ensureShimmer = () => {
  if (typeof document === "undefined" || document.getElementById("ui-skeleton-css")) return;
  const s = document.createElement("style");
  s.id = "ui-skeleton-css";
  s.textContent = "@keyframes uiShimmer{0%{background-position:-460px 0}100%{background-position:460px 0}}";
  document.head.appendChild(s);
};

const shimmerBase = {
  background: "linear-gradient(90deg,#eef2f7 8%,#e2e8f0 18%,#eef2f7 33%)",
  backgroundSize: "800px 100%",
  animation: "uiShimmer 1.4s ease infinite",
};

/* ── Skeleton : bloc de chargement générique ── */
export const Skeleton = ({ width = "100%", height = 16, radius = 8, style }) => {
  ensureShimmer();
  return <div aria-hidden="true" style={{ ...shimmerBase, width, height, borderRadius: radius, ...style }} />;
};

/* ── SkeletonText : n lignes de largeurs variées ── */
export const SkeletonText = ({ lines = 3, gap = 10, lastWidth = "60%" }) => (
  <div style={{ display: "flex", flexDirection: "column", gap }}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} height={12} width={i === lines - 1 ? lastWidth : "100%"} />
    ))}
  </div>
);

/* ── SkeletonCard : carte (avatar + titre + lignes) ── */
export const SkeletonCard = () => (
  <div style={{ border: "1px solid #eef2f7", borderRadius: 12, padding: 16, background: "#fff" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
      <Skeleton width={44} height={44} radius="50%" />
      <div style={{ flex: 1 }}><Skeleton height={14} width="55%" style={{ marginBottom: 8 }} /><Skeleton height={10} width="35%" /></div>
    </div>
    <SkeletonText lines={3} />
  </div>
);

/* ── SkeletonTable : lignes/colonnes de tableau ── */
export const SkeletonTable = ({ rows = 5, cols = 4 }) => (
  <div role="status" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
        {Array.from({ length: cols }).map((__, c) => <Skeleton key={c} height={14} width={c === 0 ? "70%" : "90%"} />)}
      </div>
    ))}
  </div>
);

/* ── EmptyState : liste/écran sans données ── */
export const EmptyState = ({ icon = "📭", title, description, action }) => {
  const { t } = useTranslation("common");
  return (
    <div role="status" style={{ textAlign: "center", padding: "48px 24px", color: "#6b7280" }}>
      <div style={{ fontSize: 56, marginBottom: 12, opacity: 0.85 }} aria-hidden="true">{icon}</div>
      <h3 style={{ margin: "0 0 6px", color: NAVY, fontSize: 18 }}>{title || t("states.empty")}</h3>
      <p style={{ margin: "0 auto 16px", maxWidth: 420, fontSize: 14 }}>{description || t("states.emptyDesc")}</p>
      {action}
    </div>
  );
};

/* ── ErrorState : échec de chargement + réessayer ── */
export const ErrorState = ({ title, message, onRetry, retryLabel }) => {
  const { t } = useTranslation("common");
  return (
    <div role="alert" style={{ textAlign: "center", padding: "40px 24px" }}>
      <div style={{ fontSize: 52, marginBottom: 10 }} aria-hidden="true">⚠️</div>
      <h3 style={{ color: "#dc2626", margin: "0 0 6px", fontSize: 18 }}>{title || t("states.error")}</h3>
      <p style={{ color: "#6b7280", fontSize: 14, maxWidth: 440, margin: "0 auto 16px" }}>{message || t("states.errorDesc")}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}
          style={{ background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
          {retryLabel || t("btn.retry")}
        </button>
      )}
    </div>
  );
};

/* ── OfflineState : bannière hors-ligne (auto navigator.onLine) ── */
export const OfflineState = ({ message }) => {
  const { t } = useTranslation("common");
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (online) return null;
  return (
    <div role="status" style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d",
      borderRadius: 8, padding: "10px 14px", fontSize: 13, margin: "0 0 12px",
    }}>
      <span aria-hidden="true">📴</span> {message || `${t("states.offline")} — ${t("states.offlineDesc")}`}
    </div>
  );
};
